import {
	AbstractControl,
	FormArray,
	FormControl,
	FormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';

import {
	catchError,
	filter,
	map,
	Observable,
	of,
	startWith,
	Subscriber,
	switchMap,
	tap,
	throwError,
} from 'rxjs';

import { CheckboxField, CustomFormControlWithField } from '@rs-bank/rs-forms-extention';
import {
	Button,
	Column,
	ComponentBox,
	FieldBoxForFormControl,
	FieldBoxWithCustomFormControl,
	Form,
	Row,
} from '@rs-bank/rs-layout';
import { unsubscribeOnDestroy } from '@rs-bank/rs-rxjs-extention';


import { AttentionComponent } from 'ib/internetbank-core/ib-components/attention/attention.component';
import { DateInterval } from 'ib/internetbank-core/ib-components/date-picker/date-interval';
import { ValueType } from 'ib/internetbank-core/ib-components/date-picker/date-picker.component';
import { ApplicationException } from 'ib/internetbank-core/ib-exceptions/ib-exceptions';
import { DateUtils } from 'ib/internetbank-core/utils/date-utils';
import { IbDateInputField } from 'ib/internetbank-fields/field-types/date-input.ib.field';
import { IbInformationField } from 'ib/internetbank-fields/field-types/information.ib.field-types';
import { IbRadioButtonField } from 'ib/internetbank-fields/field-types/radio-button.ib.field-type';
import { IbSelectField } from 'ib/internetbank-fields/field-types/select.ib.field-types';


import { CustomValidators } from '../../../../../common/custom-validation/custom-validators';
import { ProductId } from '../../../../../common/models/product-id';
import { Product } from '../../../../../products/products.model';
import { ProductSelectItemComponent } from '../../../../../shared/components/product-select/product-select-item/product-select-item.component';
import { WizardDataStep } from '../../../../../shared/wizard-v2/wizard-core/steps/base-step-types/wizard-data-step';
import { ReferencesOrderProductSelectValueEmptyComponent } from '../../references-group-wizard/components/references-order-product-select-value-empty/references-order-product-select-value-empty.component';
import { ReferencesGroupWizardComponent } from '../../references-group-wizard/references-group-wizard.component';
import {
	Reference,
	ReferenceDetailInfo,
	ReferencesCreateBase,
	ReferencesCreateBaseParams,
	ReferencesForm,
	SelectAccountChooseTypeEnum,
} from '../../references.entities';
import { ReferencesWizardBaseComponent } from '../reference-wizard.base';

export class ReferenceFormStep extends WizardDataStep<ReferencesWizardBaseComponent, Reference> {


	public override newForm: FormGroup<ReferencesForm> = new FormGroup({
		selectAccountChooseType: new FormControl<SelectAccountChooseTypeEnum>(SelectAccountChooseTypeEnum.ONE_ACCOUNT, { nonNullable: true }),
		sourceSelect: new FormControl<Product | null>(null, [Validators.required]),
		productsWidthCheckboxes: new FormArray<FormControl<Product>>(
			[],
			(c: AbstractControl): ValidationErrors | null => {
				if (!c.value.length) {
					return { required: true };
				}
				return null;
			},
		),
		period: new FormControl(new DateInterval(undefined, undefined), { nonNullable: true }),
		allPeriod: new FormControl(false, { nonNullable: true }),
	});

	private readonly stepTitle = 'Заказ справки';

	private referenceNumber: number | undefined;
	private referenceName: string | undefined;


	public override onInit(reference: Reference): Observable<void> {
		this.wizard.title = this.stepTitle;
		this.stepInformation.defaultButtonsHidden.next(true);
		this.goBackVisibleForStep = this.wizardComponent instanceof ReferencesGroupWizardComponent;

		if (this.stepInformation.operationErrors.value) {
			this.stepInformation.operationErrors.next(undefined);
		}

		if (this.referenceNumber === reference.referenceNumber) {
			return of(undefined);
		}

		this.referenceNumber = reference.referenceNumber;
		this.referenceName = reference.referenceName;

		this.layoutForm?.hide();

		return this.wizardComponent.referencesFacadeService.getReferenceInfo(reference.referenceNumber, this.wizardComponent.routerParameters.productContractId)
			.pipe(
				catchError((error: ApplicationException) => {
					this.referenceName = undefined;
					this.referenceNumber = undefined;

					return throwError(() => error);
				}),
				switchMap((detail: ReferenceDetailInfo) => this.createLayout(detail)),
				tap(() => this.layoutForm.show()),
			);
	}

	public override onExecute(): Observable<void> {
		return of(undefined);
	}


	public override prepareBack(): Observable<void> {
		this.wizard.title = this.stepTitle;

		return super.prepareBack();
	}

	private createLayout(data: ReferenceDetailInfo): Observable<void> {
		return new Observable((subscriber: Subscriber<void>) => {

			this.newForm = new FormGroup<ReferencesForm>({
				selectAccountChooseType: new FormControl<SelectAccountChooseTypeEnum>(SelectAccountChooseTypeEnum.ONE_ACCOUNT, { nonNullable: true }),
				sourceSelect: new FormControl<Product | null>(null, [Validators.required]),
				productsWidthCheckboxes: new FormArray<FormControl<Product>>(
					[],
					(c: AbstractControl): ValidationErrors | null => {
						if (!c.value.length) {
							return { required: true };
						}
						return null;
					},
				),
				period: new FormControl(new DateInterval(undefined, undefined), { nonNullable: true }),
				allPeriod: new FormControl(!!data.params?.dateall, { nonNullable: true }),
			});

			const isVisibleAllAccountsRadio: boolean = !!data.quantity && data.products.length > 1 && !data.byClient;
			const disabledButtons$: Observable<boolean> = this.inLoading$$.asObservable()
				.pipe(
					switchMap((value: boolean) => {
						if (value) {
							return of(value);
						}

						return !data.products.length
							? of(true)
							: this.newForm.statusChanges
								.pipe(
									map(() => {
										switch (this.newForm.controls.selectAccountChooseType.value) {
											case SelectAccountChooseTypeEnum.ALL_ACCOUNTS:
												return this.newForm.controls.productsWidthCheckboxes.invalid || this.newForm.controls.allPeriod.invalid || this.newForm.controls.period.invalid;

											case SelectAccountChooseTypeEnum.ONE_ACCOUNT:
												return this.newForm.controls.sourceSelect.invalid || this.newForm.controls.allPeriod.invalid || this.newForm.controls.period.invalid;

											default:
												return this.newForm.controls.allPeriod.invalid || this.newForm.controls.period.invalid;
										}
									}),
								)
					}),
				);

			let sortedProducts: Array<Product>;

			if (this.wizardComponent.routerParameters.productContractId) {
				sortedProducts = data.products.sort((a: Product, b: Product) =>
					ProductId.createIdByProdId(a.productId)?.getSimpleId() === +this.wizardComponent.routerParameters.productContractId! ? -1 : 1,
				);
			} else {
				sortedProducts = data.products;
			}

			let sourceSelectFieldBox: FieldBoxForFormControl<IbSelectField<Product>>;
			let sourceWithCheckboxLayout: Column | undefined;


			this.layoutForm = new Form(
				data.referenceDescr,
				new Column([

					(() => {
						if (isVisibleAllAccountsRadio) {
							return new Column([
								new FieldBoxForFormControl(
									this.newForm.controls.selectAccountChooseType,
									new IbRadioButtonField({
										items: [
											{
												label: 'По счету',
												value: SelectAccountChooseTypeEnum.ONE_ACCOUNT,
											},
											{
												label: 'Все счета',
												value: SelectAccountChooseTypeEnum.ALL_ACCOUNTS,
											},
										],
										buttonType: 'radio',
									}),

								),
								sourceWithCheckboxLayout = this.getSourceWithCheckboxLayout(data.products),
							]);
						}

						return;
					})(),

					(() => {
						if (data.byClient) {
							return;
						}

						sourceSelectFieldBox = new FieldBoxForFormControl(
							this.newForm.controls.sourceSelect,
							new IbSelectField({
								label: isVisibleAllAccountsRadio ? '' : 'По счету',
								itemComponent: ProductSelectItemComponent,
								emptyValueComponent: ReferencesOrderProductSelectValueEmptyComponent,
								options$: of(sortedProducts)
									.pipe(
										tap((products: Array<Product>) => {
											this.newForm?.controls.sourceSelect.setValue(products[0]);
											if (products.length <= 1) {
												this.newForm?.controls.sourceSelect.disable();
											}
										}),
									),
							}),
							{ cssClassList: ['long-field-wrapper'] },
						);

						return sourceSelectFieldBox;
					})(),
					new FieldBoxForFormControl(
						this.newForm.controls.period,
						new IbDateInputField({
							label: 'Период формирования выписки',
							datePickerOptions: {
								valueType: ValueType.interval,
								minDate: DateUtils.getBeginningOfToday(new Date(2000, 0, 1)),
								maxDate: DateUtils.getBeginningOfToday(new Date()),
								blockPreviousDays: false,
							},
						}),
						{ visible: !!data.params || false },
					),
					new FieldBoxForFormControl(
						this.newForm.controls.allPeriod,
						new CheckboxField({ label: 'За все время' }),
						{ visible: !!data.params || false },
					),
					new FieldBoxWithCustomFormControl(
						new CustomFormControlWithField(
							new IbInformationField({ label: 'Плата за предоставление услуги «Заказ справки»' }),
							'Не взимается',
						),
					),
					new FieldBoxWithCustomFormControl(
						new CustomFormControlWithField(
							new IbInformationField({
								label: 'Адрес электронной почты',
								hints: { underLine: !this.wizardComponent.userEmail ? '<span class="error">Укажите e-mail адрес</span>' : undefined },
							}),
							this.wizardComponent.userEmail || 'отсутствуют данные',
						),
					),
					new ComponentBox(
						AttentionComponent,
						(componentInstance: AttentionComponent) => {
							componentInstance.text = 'Указать/изменить адрес электронной почты можно в разделе <a class="link" href="/settings/tab1">"Настройки - Персональная информация"</a>.'
						},
					),

				]),
				new Row([
					new Button(
						'Отправить по&nbsp;почте',
						() => {
							this.onExecute = this.createEmail;
							this.wizard.steps[this.wizard.steps.length - 1].stepInformation.headers.next([`Справка отправлена на адрес электронной почты ${this.wizardComponent.userEmail}`]);

							this.wizard.execute();
						},
						of(this.wizardComponent.userEmail)
							.pipe(
								switchMap((email: string | undefined) => {
									if (!email) {
										return of(true);
									}

									return disabledButtons$;
								}),
							),
					),
					new Button(
						'Открыть',
						() => {
							this.onExecute = this.createPrint;
							this.wizard.steps[this.wizard.steps.length - 1].stepInformation.headers.next(['']);

							this.wizard.execute();
						},
						disabledButtons$,
					),
				]),
			);

			const sourceSelect: FormControl<Product | null> = this.newForm.controls.sourceSelect;
			const periodControl: FormControl<DateInterval> = this.newForm.controls.period;
			const allPeriodControl: FormControl<boolean> = this.newForm.controls.allPeriod;

			allPeriodControl.setValue(data.params?.dateall || false);

			if (isVisibleAllAccountsRadio) {
				this.newForm.controls.selectAccountChooseType.valueChanges
					.pipe(
						tap((value: SelectAccountChooseTypeEnum) => {
							sourceWithCheckboxLayout!.visible = value === SelectAccountChooseTypeEnum.ALL_ACCOUNTS;
							sourceSelectFieldBox.visible = value === SelectAccountChooseTypeEnum.ONE_ACCOUNT;
						}),
						unsubscribeOnDestroy(this.wizardComponent),
					)
					.subscribe();
			}

			allPeriodControl.valueChanges
				.pipe(
					startWith(allPeriodControl.value),
					tap((value: boolean) => {
						if (value) {
							if (sourceSelect.value) {
								this.updateFormDates(sourceSelect.value, periodControl as CustomFormControlWithField<IbDateInputField>);
							}

							periodControl.disable();
						} else {
							periodControl.enable();
						}
					}),
					unsubscribeOnDestroy(this.wizardComponent),
				)
				.subscribe();

			if (data.params) {
				this.newForm.controls.sourceSelect.valueChanges
					.pipe(
						filter((product: Product) => !!product),
						tap((product: Product) => {
							this.updateFormDates(product, periodControl as CustomFormControlWithField<IbDateInputField>)
						}),
						unsubscribeOnDestroy(this.wizardComponent),
					)
					.subscribe();
			}


			subscriber.next();
			subscriber.complete();
		});
	}

	private getSourceWithCheckboxLayout(products: Array<Product>): Column {
		const productsColumn = new Column([], { visible: false });

		products.forEach((product: Product) => {
			const checkboxControl: FormControl<boolean> = new FormControl(false, { nonNullable: true });

			checkboxControl.valueChanges
				.pipe(
					tap((value: boolean) => {
						if (value) {
							this.newForm?.controls.productsWidthCheckboxes.push(new FormControl(product, { nonNullable: true }))
						} else {
							const index: number = this.newForm?.controls.productsWidthCheckboxes.value.findIndex(
								(prod: Product) => ProductId.createIdByProdId(prod.productId)?.equals(ProductId.createIdByProdId(product.productId)),
							);

							if (index !== -1) {
								this.newForm?.controls.productsWidthCheckboxes.removeAt(index);
							}
						}
					}),
					unsubscribeOnDestroy(this.wizardComponent),
				)
				.subscribe();

			productsColumn.children.push(
				new Row([
					new FieldBoxForFormControl(
						checkboxControl,
						new CheckboxField({}),
					),
					new Column([
						new ComponentBox(
							ProductSelectItemComponent,
							(componentInstance: ProductSelectItemComponent) => {
								componentInstance.product = product;
							},
							{
								cssClassList: [
									'product',
									'long-field-wrapper',
								],
							},
						),
						new FieldBoxWithCustomFormControl(
							new CustomFormControlWithField(
								new IbInformationField({}),
								`№${product.accountNumber}`,
							),
							{ cssClassList: ['account-number'] },
						),
					], { cssClassList: ['product-with-account-number-column'] }),
				], { cssClassList: ['products-with-checkbox-row'] }),
			);
		});

		return productsColumn;
	}

	private updateFormDates(product: Product, periodControl: CustomFormControlWithField<IbDateInputField>): void {
		const productCreateDate: string = product.createDt || product.signedDate;
		const contractDate: Date = DateUtils.getBeginningOfToday(productCreateDate ? new Date(productCreateDate) : new Date());
		const today = DateUtils.getBeginningOfToday(new Date());

		periodControl.fieldContext.datePickerOptions.minDate = contractDate;

		periodControl.setValidators([
			(c: AbstractControl<DateInterval>): ValidationErrors | null => {
				if (!c.value.from || !c.value.to) {
					return { required: true }
				}

				return null;
			},
			(c: AbstractControl) => CustomValidators.regularDatesPeriod(
				contractDate,
				today,
				{ datesCount: 2 },
			)({
				...c,
				value: [
					c.value.from,
					c.value.to,
				],
			} as AbstractControl),
		]);

		periodControl.updateValueAndValidity();

		periodControl.setValue(new DateInterval(contractDate, today));

	}

	private getDataForOrderBase(): ReferencesCreateBase | undefined {
		const periodControl: FormControl<DateInterval> = this.newForm.controls.period;
		const allPeriodControl: FormControl<boolean> = this.newForm.controls.allPeriod;
		const sourceControl: FormControl<Product | null> = this.newForm.controls.sourceSelect;
		const productsControl: FormArray<FormControl<Product>> = this.newForm.controls.productsWidthCheckboxes;
		const selectAccountChooseTypeControl = this.newForm.controls.selectAccountChooseType;

		const orderCreateBody: ReferencesCreateBase = {
			referenceNumber: this.referenceNumber!,
			referenceName: this.referenceName!,
			products: selectAccountChooseTypeControl.value === SelectAccountChooseTypeEnum.ALL_ACCOUNTS
				? productsControl.value
				: sourceControl.value ? [sourceControl.value] : null,
		};
		const params: ReferencesCreateBaseParams = {
			dateFrom: periodControl.value.from ? DateUtils.fromJsDateToDbDate(periodControl.value.from)! : undefined,
			dateTo: periodControl.value.to ? DateUtils.fromJsDateToDbDate(periodControl.value.to)! : undefined,
			dateAll: allPeriodControl.value,
		};

		return {
			...orderCreateBody,
			params: params.dateAll ? { dateAll: 1 } : {
				dateFrom: params.dateFrom,
				dateTo: params.dateTo,
			},
		};

	}

	private createEmail(): Observable<void> {
		return this.wizardComponent.referencesFacadeService.orderEnquiryCreate(this.getDataForOrderBase())
			.pipe(
				switchMap((value: { actionId: number }) => this.wizardComponent.referencesFacadeService.orderEnquiryEmail(`${value.actionId}`, this.wizardComponent.userEmail!)),
			);
	}

	private createPrint(): Observable<void> {
		const isIOS: boolean = this.wizardComponent.deviceInformationService.iOS();
		let win: WindowProxy | null = isIOS ? window.open(`${location.origin}/waiting`, '_blank') : null;


		return this.wizardComponent.referencesFacadeService.orderEnquiryCreate(this.getDataForOrderBase())
			.pipe(
				switchMap((value: { actionId: number }) => this.wizardComponent.referencesFacadeService.orderEnquiryPrint(`${value.actionId}`)),
				tap((blob: Blob) => {
					const url = URL.createObjectURL(blob);

					if (isIOS) {
						win?.location.replace(url);
					} else {
						win = window.open(url);
					}

					win?.focus();
				}),
				catchError((error: ApplicationException) => {
					win?.close();

					return throwError(() => error);
				}),
				map(() => undefined),
			)
	}

}